import type { ThemeConfig } from "@/types/theme";

export const tailwindTeal: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-teal-700 dark:text-teal-400",
    secondary: "text-teal-900 dark:text-teal-500",
    accent: "text-cyan-600 dark:text-cyan-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-teal-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-teal-600 dark:bg-teal-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-teal-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-teal-600 dark:bg-teal-500",
    backgroundSecondaryColor: "bg-teal-800 dark:bg-teal-700",
    backgroundAccentColor: "bg-cyan-500 dark:bg-cyan-400",
    backgroundPrimarySubtle: "bg-teal-50 dark:bg-teal-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-teal-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-teal-700 dark:text-teal-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-teal-600 dark:text-teal-400",
    iconBackgroundFilled: "bg-teal-100 dark:bg-teal-950",
    iconBackgroundGradient: "bg-gradient-to-br from-teal-500 to-cyan-500",
    iconBackgroundDefault: "bg-teal-50 dark:bg-teal-950/50",
    iconBorder: "border-teal-200 dark:border-teal-800",

    // Border classes
    borderDefault: "border-teal-200 dark:border-gray-700",
    borderLight: "border-teal-100 dark:border-gray-800",
    borderStrong: "border-teal-300 dark:border-gray-600",
    borderPrimary: "border-teal-700 dark:border-teal-500",
    borderSecondary: "border-teal-900 dark:border-teal-700",
    borderBottom: "border-b border-teal-200 dark:border-gray-700",
    borderAccent: "border-teal-600 dark:border-teal-400",

    // Badge classes
    badgeBackground: "bg-teal-100 dark:bg-teal-950",
    badgeText: "text-teal-700 dark:text-teal-300",

    // Shadow classes
    shadowPrimary: "shadow-teal-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-teal-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(20,184,166,0.30)]", // teal-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-teal-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-teal-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-teal-600 hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-teal-400 dark:bg-teal-600",
    textEmphasis: "text-rose-600 dark:text-rose-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-teal-600 to-teal-700",
    gradientSecondary: "bg-gradient-to-br from-teal-700 to-teal-900",
    gradientAccent: "bg-gradient-to-r from-teal-500 to-cyan-500",
    gradientSubtle:
      "bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant: "bg-gradient-to-r from-teal-600 via-cyan-500 to-teal-600",
    gradientHero: "bg-gradient-to-br from-teal-600 via-cyan-500 to-teal-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
